import { useMemo } from "react";
import { format, isToday } from "date-fns";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Event } from "@/hooks/useEvents";
import { EventBlock } from "./EventBlock";
import {
  generateTimeSlots,
  calculateEventPosition,
  detectOverlaps,
  getEventsForDay,
  getThreeDays,
  formatHourLabel,
  HOUR_HEIGHT,
  DAY_START_HOUR,
  VISIBLE_HOURS,
} from "@/lib/calendarUtils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

interface ThreeDayViewProps {
  date: Date;
  events: Event[];
  onEventSelect: (event: Event) => void;
  onCreateEvent?: (startTime: Date, endTime: Date) => void;
}

export function ThreeDayView({ date, events, onEventSelect, onCreateEvent }: ThreeDayViewProps) {
  const timeSlots = useMemo(() => generateTimeSlots(), []);
  const days = useMemo(() => getThreeDays(date), [date]);

  const handleSlotClick = (day: Date, hour: number) => {
    if (!onCreateEvent) return;
    
    const startTime = new Date(day);
    startTime.setHours(hour, 0, 0, 0);
    
    const endTime = new Date(startTime);
    endTime.setHours(hour + 1, 0, 0, 0);
    
    onCreateEvent(startTime, endTime);
  };

  const getCurrentTimeTop = (day: Date): number | null => {
    if (!isToday(day)) return null;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    if (currentHour < DAY_START_HOUR || currentHour >= DAY_START_HOUR + VISIBLE_HOURS) {
      return null;
    }
    
    return ((currentHour - DAY_START_HOUR) * HOUR_HEIGHT) + (currentMinutes / 60 * HOUR_HEIGHT);
  };

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-[80px_repeat(3,1fr)] border-b bg-muted/50">
        <div className="border-r" />
        {days.map((day) => (
          <div
            key={day.toISOString()}
            className={cn(
              "p-4 border-r last:border-r-0 text-center",
              isToday(day) && "bg-primary/10"
            )}
          >
            <div className={cn(
              "text-sm font-medium",
              isToday(day) && "text-primary"
            )}>
              {format(day, "EEE")}
            </div>
            <div className={cn(
              "text-2xl font-bold",
              isToday(day) && "text-primary"
            )}>
              {format(day, "d")}
            </div>
          </div>
        ))}
      </div>

      {/* Time Grid */}
      <ScrollArea className="h-[600px]">
        <div className="relative">
          <div className="grid grid-cols-[80px_repeat(3,1fr)]">
            {/* Time Labels Column */}
            <div>
              {timeSlots.map((slot, index) => (
                <div
                  key={index}
                  className="h-[60px] border-b border-r flex items-start justify-end pr-2 pt-1 text-xs text-muted-foreground"
                >
                  {formatHourLabel(slot.getHours())}
                </div>
              ))}
            </div>

            {/* Day Columns */}
            {days.map((day) => {
              const dayEvents = getEventsForDay(events, day);
              const eventsWithColumns = detectOverlaps(dayEvents);
              const currentTimeTop = getCurrentTimeTop(day);

              return (
                <div key={day.toISOString()} className="relative border-r last:border-r-0">
                  {/* Time Slot Grid */}
                  {timeSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="h-[60px] border-b hover:bg-accent/50 cursor-pointer group relative"
                      onClick={() => handleSlotClick(day, slot.getHours())}
                    >
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" className="h-6 text-xs">
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Current Time Indicator */}
                  {currentTimeTop !== null && (
                    <div
                      className="absolute left-0 right-0 border-t-2 border-red-500 z-10"
                      style={{ top: `${currentTimeTop}px` }}
                    >
                      <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-red-500" />
                    </div>
                  )}

                  {/* Events */}
                  {eventsWithColumns.map((event) => {
                    const { top, height } = calculateEventPosition(
                      new Date(event.start_time),
                      new Date(event.end_time),
                      day
                    );
                    
                    const width = 100 / event.totalColumns;
                    const left = event.column * width;

                    return (
                      <EventBlock
                        key={event.id}
                        event={event}
                        top={top}
                        height={height}
                        width={width}
                        left={left}
                        onClick={onEventSelect}
                        viewType="3day"
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
